import type { DateRange, DateRangeParsed } from "@/ui/components/date/types";

import { isValid, parse } from "chartjs-graph";

/**
 * Each end of a date range is in one of three states:
 * - `filled`: the user entered a fixed or dynamic date expression
 * - `unbounded`: absent on purpose, so the range is open at that end
 * - `empty`: absent but required, i.e. the range is only half entered
 */
export type BoundState = "empty" | "unbounded" | "filled";

/**
 * An absent bound (`null`, `undefined` or `""`) is open-ended when the picker is
 * nullable and merely incomplete otherwise. `null` is the canonical absent value
 * a nullable picker writes, but a field the user emptied by hand reads the same.
 */
export const boundState = (
	raw: string | null | undefined,
	nullable: boolean
): BoundState => {
	if (!raw) {
		return nullable ? "unbounded" : "empty";
	}
	return "filled";
};

/**
 * Mirrors {@link boundState} onto the parsed model: `null` stays unbounded,
 * a missing value stays undefined, and junk input (i.e. now/MMMM) fails to
 * undefined so it surfaces as invalid.
 *
 * chartjs-graph bundles its own dayjs copy, so the `Dayjs` it returns is
 * structurally distinct from the app's plugin-augmented `Dayjs` even though both
 * are dayjs objects. The cast keeps that quirk in one place instead of at every
 * call site.
 */
export const parseBound = (
	raw: string | null | undefined,
	isEnd = false
): DateRangeParsed["from"] => {
	if (raw === null) {
		return null;
	}
	if (!raw) {
		return undefined;
	}
	return (parse(raw, isEnd) as DateRangeParsed["from"]) ?? undefined;
};

/**
 * Re-resolves a stored period's parsed bounds from its `from` / `to` expressions.
 * The stored `from_parsed` / `to_parsed` are a snapshot of when the period was
 * picked, so a dynamic expression (i.e. now-7d/d) goes stale the next day.
 */
export const resolvePeriod = <
	T extends {
		from: string | null;
		to: string | null;
		from_parsed?: Date | null;
		to_parsed?: Date | null;
	},
>(
	period: T
): T & { from_parsed: Date | null; to_parsed: Date | null } => ({
	...period,
	from_parsed: parseBound(period.from)?.toDate() ?? null,
	to_parsed: parseBound(period.to, true)?.toDate() ?? null,
});

/**
 * A range is valid when both ends are unbounded or parse to a valid date, and —
 * only when both ends are bounded — `from` comes before `to`. An entirely empty
 * range is neutral rather than an error; a half-entered one is invalid.
 */
export const isDateRangeValid = (
	model: DateRange | undefined,
	parsed: DateRangeParsed | undefined,
	nullable: boolean
): boolean => {
	const from = boundState(model?.from, nullable);
	const to = boundState(model?.to, nullable);

	// Nothing entered yet — neutral, not an error
	if (from === "empty" && to === "empty") {
		return true;
	}

	// Half-entered is invalid
	if (from === "empty" || to === "empty") {
		return false;
	}

	const isFromValid = from === "unbounded" || isValid(parsed?.from);
	const isToValid = to === "unbounded" || isValid(parsed?.to);
	if (!isFromValid || !isToValid) {
		return false;
	}

	// Order only means anything when both ends are bounded
	if (from === "filled" && to === "filled") {
		return parsed.from.isBefore(parsed.to);
	}

	return true;
};